import { Clock, CheckCircle, XCircle, Undo2, RotateCcw, Send, Rocket, ArchiveRestore } from 'lucide-react';

/**
 * Shared constants + derivation helpers for the review/attestation history
 * surfaces (FECOA-1611): the at-a-glance modal (`ReviewHistory`) and the full
 * dedicated audit page (`ReviewHistoryPage`). Both read the same
 * ReviewSubmission records; keeping the meta maps and cycle-prep logic here
 * means the two views can never drift on labels, colors, or version semantics.
 */

// The submission half of a cycle — always present.
export const SUBMIT_META = {
  label: 'Submitted for review',
  icon: Send,
  cls: 'text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-300 dark:bg-blue-900/30 dark:border-blue-800',
};

// The decision half of a cycle, keyed by the submission's terminal status.
export const DECISION_META = {
  approved: { label: 'Approved', icon: CheckCircle, cls: 'text-teal-700 bg-teal-50 border-teal-200 dark:text-teal-300 dark:bg-teal-900/30 dark:border-teal-800' },
  denied: { label: 'Denied', icon: XCircle, cls: 'text-red-600 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-900/30 dark:border-red-800' },
  withdrawn: { label: 'Withdrawn', icon: Undo2, cls: 'text-gray-600 bg-gray-50 border-gray-200 dark:text-gray-300 dark:bg-gray-800 dark:border-gray-700' },
  revoked: { label: 'Approval revoked', icon: RotateCcw, cls: 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-900/30 dark:border-amber-800' },
  published: { label: 'Published', icon: Rocket, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-900/30 dark:border-emerald-800' },
  unpublished: { label: 'Unpublished', icon: ArchiveRestore, cls: 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-900/30 dark:border-amber-800' },
};

// Statuses that are a single standalone action rather than the decision half of
// a submit->decide cycle. These records are created already-decided by one
// actor (FECOA-1674 revoke, FECOA-1694 publish), so they render as one event
// with no phantom "Submitted for review" row preceding them.
export const STANDALONE_STATUSES = new Set(['published', 'revoked', 'unpublished']);

// Placeholder decision node shown while a submission is still pending, so the
// cycle always has two nodes and doesn't look truncated.
export const AWAITING_META = {
  label: 'Awaiting decision',
  icon: Clock,
  cls: 'text-gray-400 bg-white border-gray-300 border-dashed dark:text-gray-500 dark:bg-gray-800 dark:border-gray-600',
};

// Header chip mirroring the entity's card badge, so the surface's story matches
// what the user clicked from. Keyed by the entity's current status.
export const CURRENT_STATUS_META = {
  draft: { label: 'Draft', cls: 'text-gray-600 bg-gray-100 border-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600' },
  in_review: { label: 'Pending Review', cls: 'text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-300 dark:bg-blue-900/30 dark:border-blue-800' },
  pending: { label: 'Pending Review', cls: 'text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-300 dark:bg-blue-900/30 dark:border-blue-800' },
  approved: { label: 'Approved', cls: 'text-teal-700 bg-teal-50 border-teal-200 dark:text-teal-300 dark:bg-teal-900/30 dark:border-teal-800' },
  published: { label: 'Published', cls: 'text-teal-700 bg-teal-50 border-teal-200 dark:text-teal-300 dark:bg-teal-900/30 dark:border-teal-800' },
  denied: { label: 'Needs Revision', cls: 'text-red-600 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-900/30 dark:border-red-800' },
  archived: { label: 'Archived', cls: 'text-gray-500 bg-gray-100 border-gray-200 dark:text-gray-400 dark:bg-gray-700 dark:border-gray-600' },
};

// Plain-English explanation of the entity_version hash, shown on hover.
export const VERSION_TOOLTIP =
  'Unique system identifier for the exact content that was reviewed. '
  + 'If you ever need to trace this approval, or flag it as made in error, '
  + 'share this ID with support and they can pull up the full record.';

export const fmt = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

export const ts = (value) => {
  const t = value ? new Date(value).getTime() : 0;
  return Number.isNaN(t) ? 0 : t;
};

// A withdrawal is the submitter recalling their own submission, not an approver
// decision — fall back to the submitter for the decider fields, and prefer the
// frozen signature name so the record stays consistent even if the approver
// later renames.
export const deciderOf = (r) => ({
  name: r.signed_by_name
    || r.decided_by_name
    || (r.status === 'withdrawn' ? r.submitted_by_name : null)
    || 'Unknown',
  email: r.status === 'withdrawn' ? r.submitted_by_email : r.signed_by_email,
});

// Sort oldest-first and attach version-change info to each cycle. We only
// surface the version when it changes: the hash on the first submission and on
// any resubmission whose content differs from the prior one; identical
// resubmissions collapse to a muted "unchanged" note. A repeated hash is noise
// — what a reviewer wants is "did anything change since I last saw this?".
// Returns `{ r, ci, decided, decisionMeta, version }` per cycle, where
// `version.kind` is one of 'none' | 'reviewed' | 'changed' | 'unchanged'.
// The returned order is chronological (version diffing depends on it);
// display surfaces reverse it so the newest activity renders on top (FECOA-1599).
export const prepareCycles = (rows) => {
  const cycles = [...(rows || [])].sort((a, b) => ts(a.submitted_at) - ts(b.submitted_at));
  return cycles.map((r, ci) => {
    const prevVersion = ci > 0 ? cycles[ci - 1].entity_version : null;
    const versionChanged = ci === 0 || r.entity_version !== prevVersion;
    let version;
    if (!r.entity_version) {
      version = { kind: 'none' };
    } else if (versionChanged) {
      version = { kind: ci === 0 ? 'reviewed' : 'changed', hash: String(r.entity_version).slice(0, 8) };
    } else {
      version = { kind: 'unchanged' };
    }
    const decided = r.is_decided;
    const decisionMeta = decided ? (DECISION_META[r.status] || DECISION_META.approved) : AWAITING_META;
    const standalone = STANDALONE_STATUSES.has(r.status);
    return { r, ci, decided, decisionMeta, version, standalone };
  });
};
